import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutDashboard, KanbanSquare, Users, UserCog, RefreshCw, LogOut, ExternalLink, Bell, BellRing, KeyRound, CircleUserRound, ShieldCheck, CalendarDays } from "lucide-react";

export const ADMIN_NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "pipeline", label: "Pipeline", icon: KanbanSquare },
  { key: "recruits", label: "Recruits", icon: Users },
  { key: "agents", label: "Agents", icon: UserCog },
  { key: "admins", label: "Admins", icon: ShieldCheck },
  { key: "calendly", label: "Calendly", icon: CalendarDays },
];

export const AGENT_NAV = [
  { key: "dashboard", label: "My Dashboard", icon: LayoutDashboard },
  { key: "pipeline", label: "My Pipeline", icon: KanbanSquare },
  { key: "recruits", label: "My Recruits", icon: Users },
];

function Brand({ label }) {
  return (
    <div className="flex items-center justify-center px-4 pt-4 pb-2" data-testid="admin-brand">
      <img src="/crm-logo.png" alt={`Breeze ${label}`} className="h-20 w-auto object-contain drop-shadow-[0_0_16px_rgba(0,198,239,0.35)]" />
    </div>
  );
}

function SideNav({ nav, view, setView, counts, onNavigate }) {
  return (
    <nav className="flex-1 px-3 py-2" data-testid="admin-sidenav">
      <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Workspace</div>
      {nav.map((n) => {
        const active = view === n.key;
        const count = counts[n.key];
        return (
          <button
            key={n.key}
            onClick={() => { setView(n.key); onNavigate?.(); }}
            data-testid={`nav-${n.key}`}
            className={`mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${active ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}
          >
            <n.icon className={`h-4 w-4 ${active ? "text-[#00C6EF]" : ""}`} />
            <span className="flex-1 text-left">{n.label}</span>
            {count != null && <span className={`font-mono-num rounded-md px-1.5 py-0.5 text-[10px] ${active ? "bg-[#00C6EF]/20 text-[#00C6EF]" : "bg-white/5 text-slate-500"}`}>{count}</span>}
          </button>
        );
      })}
    </nav>
  );
}

export default function AdminShell({ nav = ADMIN_NAV, brandLabel = "Recruiting CRM", view, setView, counts, user, loading, onRefresh, onLogout, onChangePassword, onNotifications, onCalendly, pushOn, newCount, children }) {
  const [open, setOpen] = useState(false);
  const current = nav.find((n) => n.key === view);

  const sidebar = (
    <div className="flex h-full flex-col bg-[#0b1528]">
      <Brand label={brandLabel} />
      <SideNav nav={nav} view={view} setView={setView} counts={counts} onNavigate={() => setOpen(false)} />
      <div className="border-t border-white/10 p-3">
        <a href="/" target="_blank" rel="noreferrer" data-testid="nav-view-funnel" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-white/5 hover:text-white transition-colors">
          <ExternalLink className="h-4 w-4" /> View live funnel
        </a>
        {onNotifications && (
          <button onClick={onNotifications} data-testid="nav-notifications" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-white/5 hover:text-white transition-colors">
            {pushOn ? <BellRing className="h-4 w-4 text-emerald-400" /> : <Bell className="h-4 w-4" />} Notifications
            <span className={`ml-auto rounded-md px-1.5 py-0.5 text-[10px] font-bold ${pushOn ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-slate-500"}`}>{pushOn ? "ON" : "OFF"}</span>
          </button>
        )}
        {onCalendly && (
          <button onClick={onCalendly} data-testid="nav-calendly" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-white/5 hover:text-white transition-colors">
            <CalendarDays className="h-4 w-4" /> Calendly
          </button>
        )}
        {onChangePassword && (
          <button onClick={onChangePassword} data-testid="nav-change-password" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-400 hover:bg-white/5 hover:text-white transition-colors">
            <KeyRound className="h-4 w-4" /> Change password
          </button>
        )}
        <div className="mt-2 flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#00C6EF]/15 text-xs font-bold text-[#00C6EF]">{(user?.name || "A")[0]}</span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-bold text-white" data-testid="shell-user-name">{user?.name || "Admin"}</div>
            <div className="truncate text-[10px] text-slate-500">{user?.email}</div>
          </div>
          <button onClick={onLogout} data-testid="admin-logout-button" title="Sign out" className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="crm-root min-h-screen bg-[#f4f6fa] text-slate-900" data-testid="admin-dashboard" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 lg:block">{sidebar}</aside>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur pt-safe">
          <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
            <img src="/icon-192.png" alt="BFG" className="h-8 w-8 rounded-lg lg:hidden" />
            <div className="flex items-center gap-2 text-sm">
              <span className="hidden sm:inline text-slate-400">Breeze</span>
              <span className="hidden sm:inline text-slate-300">/</span>
              <span className="font-semibold text-slate-900 text-base sm:text-sm" data-testid="admin-page-title">{current?.label}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {newCount > 0 && (
                <button onClick={() => setView("recruits")} data-testid="admin-new-alert" className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 sm:px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 transition-colors">
                  <Bell className="h-3.5 w-3.5" /> {newCount}<span className="hidden sm:inline"> new to contact</span>
                </button>
              )}
              <button onClick={onRefresh} data-testid="admin-refresh-button" title="Refresh" className="flex h-10 w-10 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 transition-colors">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
        </header>
        <main className="px-4 py-5 pb-28 sm:px-6 sm:py-6 lg:px-8 lg:pb-8">{children}</main>
      </div>

      {/* Native-style bottom tab bar (phones / tablets) */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur pb-safe lg:hidden" data-testid="mobile-tabbar">
        <div className="flex h-16 items-stretch">
          {nav.slice(0, 4).map((n) => {
            const active = view === n.key;
            const count = counts[n.key];
            return (
              <button key={n.key} onClick={() => { setView(n.key); window.scrollTo({ top: 0 }); }} data-testid={`mobile-tab-${n.key}`} className={`relative flex flex-1 flex-col items-center justify-center gap-1 text-[10px] font-semibold transition-colors ${active ? "text-[#0b1528]" : "text-slate-400 active:text-slate-600"}`}>
                <span className={`relative flex h-7 w-12 items-center justify-center rounded-full transition-colors ${active ? "bg-[#00C6EF]/15" : ""}`}>
                  <n.icon className={`h-5 w-5 ${active ? "text-[#0891b2]" : ""}`} />
                  {count > 0 && <span className="font-mono-num absolute -right-0.5 -top-1 min-w-[16px] rounded-full bg-[#0b1528] px-1 text-[9px] leading-4 text-white">{count}</span>}
                </span>
                {n.label.replace(/^My /, "")}
              </button>
            );
          })}
          <button onClick={() => setOpen(true)} data-testid="mobile-tab-account" className={`flex flex-1 flex-col items-center justify-center gap-1 text-[10px] font-semibold ${open ? "text-[#0b1528]" : "text-slate-400"}`}>
            <span className={`flex h-7 w-12 items-center justify-center rounded-full ${open ? "bg-[#00C6EF]/15" : ""}`}><CircleUserRound className="h-5 w-5" /></span>
            Account
          </button>
        </div>
      </nav>

      {/* Account bottom sheet */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 z-[70] bg-slate-900/40 lg:hidden" />
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 380, damping: 36 }} className="fixed inset-x-0 bottom-0 z-[80] rounded-t-2xl bg-white pb-safe shadow-2xl lg:hidden" data-testid="account-sheet">
              <div className="sheet-handle" />
              <div className="px-5 pb-5 pt-2">
                <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0b1528] text-sm font-bold text-[#00C6EF]">{(user?.name || "A")[0]}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-slate-900">{user?.name || "Admin"}</div>
                    <div className="truncate text-xs text-slate-500">{user?.email}</div>
                  </div>
                  <span className="rounded-md bg-white border border-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{user?.role}</span>
                </div>
                <div className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200">
                  {nav.slice(4).map((n) => (
                    <button key={n.key} onClick={() => { setOpen(false); setView(n.key); window.scrollTo({ top: 0 }); }} data-testid={`sheet-nav-${n.key}`} className="flex w-full items-center gap-3 px-4 py-3.5 text-sm font-semibold text-slate-800 active:bg-slate-50"><n.icon className="h-4 w-4 text-slate-500" /> {n.label}</button>
                  ))}
                  {onNotifications && (
                    <button onClick={() => { setOpen(false); onNotifications(); }} data-testid="sheet-notifications" className="flex w-full items-center gap-3 px-4 py-3.5 text-sm font-semibold text-slate-800 active:bg-slate-50">
                      {pushOn ? <BellRing className="h-4 w-4 text-emerald-600" /> : <Bell className="h-4 w-4 text-slate-500" />} Phone notifications
                      <span className={`ml-auto rounded-md px-1.5 py-0.5 text-[10px] font-bold ${pushOn ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{pushOn ? "ON" : "OFF"}</span>
                    </button>
                  )}
                  {onCalendly && (
                    <button onClick={() => { setOpen(false); onCalendly(); }} data-testid="sheet-calendly" className="flex w-full items-center gap-3 px-4 py-3.5 text-sm font-semibold text-slate-800 active:bg-slate-50"><CalendarDays className="h-4 w-4 text-slate-500" /> Calendly integration</button>
                  )}
                  {onChangePassword && (
                    <button onClick={() => { setOpen(false); onChangePassword(); }} data-testid="sheet-change-password" className="flex w-full items-center gap-3 px-4 py-3.5 text-sm font-semibold text-slate-800 active:bg-slate-50"><KeyRound className="h-4 w-4 text-slate-500" /> Change password</button>
                  )}
                  <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3.5 text-sm font-semibold text-slate-800 active:bg-slate-50"><ExternalLink className="h-4 w-4 text-slate-500" /> View live funnel</a>
                  <button onClick={() => { setOpen(false); onLogout(); }} data-testid="sheet-logout" className="flex w-full items-center gap-3 px-4 py-3.5 text-sm font-semibold text-rose-600 active:bg-rose-50"><LogOut className="h-4 w-4" /> Sign out</button>
                </div>
                <button onClick={() => setOpen(false)} data-testid="sheet-close" className="mt-3 w-full rounded-xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 active:bg-slate-200">Close</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
